import { ALGOLIA_ATTRIBUTES } from './storyblokAlgoliaCategorySearchFiltersTypes'

const NON_BREAKING_SPACE_PATTERN = /\u{A0}/gu
const NON_ASCII_COMMA_PATTERN = /[،٬、﹐﹑，]/gu
const LIST_SEPARATOR_PATTERN = /[\n,;]/u

const trimAndFilterParts = (parts: string[]): string[] =>
  parts.map((part) => part.trim()).filter(Boolean)

const joinWithOperator = (
  parts: string[],
  operator: 'AND' | 'OR',
  options?: { shouldWrapEach?: boolean; shouldWrapWhole?: boolean },
): string => {
  const trimmedParts = trimAndFilterParts(parts)
  if (trimmedParts.length === 0) {
    return ''
  }
  if (trimmedParts.length === 1) {
    return trimmedParts[0]
  }

  const shouldWrapEach = options?.shouldWrapEach ?? false
  const shouldWrapWhole = options?.shouldWrapWhole ?? false
  const joinedParts = (
    shouldWrapEach ? trimmedParts.map((part) => `(${part})`) : trimmedParts
  ).join(` ${operator} `)

  return shouldWrapWhole ? `(${joinedParts})` : joinedParts
}

export const normalizeStringList = (input?: string): string[] => {
  if (!input) {
    return []
  }
  const normalized = input
    .replaceAll(NON_BREAKING_SPACE_PATTERN, ' ')
    .replaceAll(NON_ASCII_COMMA_PATTERN, ',')
    .trim()

  return trimAndFilterParts(normalized.split(LIST_SEPARATOR_PATTERN))
}

const isNonEmptyString = (value?: string): value is string =>
  typeof value === 'string' && value.trim().length > 0

export const normalizeStringArray = (
  values?: (string | undefined)[],
): string[] => (values ?? []).filter(isNonEmptyString)

const quoteAlgoliaValue = (value: string): string => {
  const escapedValue = value
    .replaceAll('\\', '\\\\')
    .replaceAll('"', String.raw`\"`)

  return `"${escapedValue}"`
}

export const joinWithAnd = (parts: string[]): string =>
  joinWithOperator(parts, 'AND', { shouldWrapEach: true })

export const joinWithOr = (parts: string[]): string =>
  joinWithOperator(parts, 'OR', { shouldWrapWhole: true })

export const orSameAttribute = (
  attribute: string,
  values: string[],
  operator: ':' | '=' = ':',
): string => {
  const clauses = values
    .map((value) =>
      operator === '='
        ? `${attribute}=${value}`
        : `${attribute}:${quoteAlgoliaValue(value)}`,
    )
    .filter(Boolean)
  if (clauses.length === 0) {
    return ''
  }
  if (clauses.length === 1) {
    return clauses[0]
  }

  return `(${clauses.join(' OR ')})`
}

const parseNumber = (raw?: string): number | null => {
  const trimmedValue = raw?.trim()
  if (!trimmedValue) {
    return null
  }
  const numberValue = Number(trimmedValue)

  return Number.isFinite(numberValue) ? numberValue : null
}

const parsePrice = (raw?: string): number | null => {
  const value = parseNumber(raw)

  return value === null ? null : Math.round(value * 100)
}

const resolveRangeBounds = (
  minValue: number | null,
  maxValue: number | null,
): { lowerBound: number | null; upperBound: number | null } | null => {
  if (minValue === null && maxValue === null) {
    return null
  }
  if (minValue === null) {
    return { lowerBound: null, upperBound: maxValue }
  }
  if (maxValue === null) {
    return { lowerBound: minValue, upperBound: null }
  }

  return {
    lowerBound: Math.min(minValue, maxValue),
    upperBound: Math.max(minValue, maxValue),
  }
}

const buildRangeClause = (
  attribute: string,
  minValue: number | null,
  maxValue: number | null,
): string => {
  const bounds = resolveRangeBounds(minValue, maxValue)
  if (!bounds) {
    return ''
  }

  const { lowerBound, upperBound } = bounds
  const parts: string[] = []
  if (lowerBound !== null) {
    parts.push(`${attribute} >= ${lowerBound}`)
  }
  if (upperBound !== null) {
    parts.push(`${attribute} <= ${upperBound}`)
  }

  return parts.length === 1 ? `(${parts[0]})` : `(${parts.join(' AND ')})`
}

export const buildDiscountClause = (
  minInput?: string,
  maxInput?: string,
): string =>
  buildRangeClause(
    ALGOLIA_ATTRIBUTES.discount,
    parseNumber(minInput),
    parseNumber(maxInput),
  )

export const buildPriceClause = (
  minInput?: string,
  maxInput?: string,
): string =>
  buildRangeClause(
    ALGOLIA_ATTRIBUTES.price,
    parsePrice(minInput),
    parsePrice(maxInput),
  )
